import { useEffect, useState } from 'react';

import { ApplicationHelpType } from '../../../../utilities/Constants';
import CaseHelper from '../../../../utilities/CaseHelper';
import { CaseName } from '../../../../components/case/CaseName';
import { CheckmateDialog } from '../../../../components/shared/dialog';
import CheckmateSelect from '../../../../components/shared/CheckmateSelect';
import Common from '../../../../stores/Common';
import { Help } from '../../../../components/shared/Help';
import { ICaseModel } from '../../../../interfaces/ICase';
import { ICaseResolutionModel } from '../../../../interfaces/ICaseResolutionModel';
import { IMultiSelectOption } from '../../../../interfaces/ILookup';
import { IUserModel } from '../../../../interfaces/IUser';
import { Stack } from 'react-bootstrap';
import { useCaseFileContext } from '../../../../contexts/useCaseFileContext';
import { useZoneContext } from '../../../../contexts/useZoneContext';

export interface CaseResolutionSettingsProps {
    onSettingsSave: (caseResolutionCase: ICaseResolutionModel) => void;
    onCancel: () => void;
    user: IUserModel;
    usersInZone: IUserModel[];
    caseResolutionCase: ICaseResolutionModel;
}

export interface CaseResolutionStepRoles {
    drafter: string;
    paralegal: string;
    A1: string;
    A2: string;
    attorneyQc: string;
    finalReview: string;
}

export function CaseResolutionSettings({
    onSettingsSave,
    onCancel,
    user,
    usersInZone,
    caseResolutionCase,
}: CaseResolutionSettingsProps) {
    const { currentCase, isReadOnlyMode, setIsReadOnlyMode } = useCaseFileContext();
    const zoneContext = useZoneContext();
    const [hasChanges, setHasChanges] = useState<boolean>(false);
    const [showUnsavedChangesOnCancelDialog, setShowUnsavedChangesOnCancelDialog] =
        useState<boolean>(false);
    const [currentCaseResolution, setCurrentCaseResolution] =
        useState<ICaseResolutionModel>(caseResolutionCase);

    const convertToCheckmate = (zoneUsers: IUserModel[]): IMultiSelectOption[] => {
        return zoneUsers.map((value, i) => ({
            label: value.profile?.firstName + ' ' + value.profile?.lastName,
            value: value.guid!,
            id: i,
            guid: value.guid!,
        }));
    };

    const zoneUsers = convertToCheckmate(usersInZone);

    const selectedDrafter = zoneUsers.find(
        (lu) => lu.guid === currentCaseResolution.steps[0].userId
    );
    const selectedA1 = zoneUsers.find((lu) => lu.guid === currentCaseResolution.steps[1].userId);
    const selectedA2 = zoneUsers.find((lu) => lu.guid === currentCaseResolution.steps[2].userId);
    const selectedParalegal = zoneUsers.find(
        (lu) => lu.guid === currentCaseResolution.steps[3].userId
    );
    const selectedAttorneyQC = zoneUsers.find(
        (lu) => lu.guid === currentCaseResolution.steps[4].userId
    );
    const selectedFinalReview = zoneUsers.find(
        (lu) => lu.guid === currentCaseResolution.steps[5].userId
    );

    const handleSaveButtonClick = () => {
        setHasChanges(false);
        onSettingsSave(currentCaseResolution);
    };

    const handleCancelButtonClicked = () => {
        if (hasChanges) {
            setShowUnsavedChangesOnCancelDialog(true);
        } else {
            onCancel();
        }
    };

    const handleDrafterChange = (selectedItem: IMultiSelectOption) => {
        setCurrentCaseResolution((prev) => ({
            ...prev,
            steps: prev.steps.map((step, i) =>
                i === 0 ? { ...step, userId: selectedItem?.guid ?? '' } : step
            ),
        }));
        setHasChanges(true);
    };
    const handleParalegalChange = (selectedItem: IMultiSelectOption) => {
        setCurrentCaseResolution((prev) => ({
            ...prev,
            steps: prev.steps.map((step, i) =>
                i === 3 ? { ...step, userId: selectedItem?.guid ?? '' } : step
            ),
        }));
        setHasChanges(true);
    };
    const handleAttorneyQcChange = (selectedItem: IMultiSelectOption) => {
        setCurrentCaseResolution((prev) => ({
            ...prev,
            steps: prev.steps.map((step, i) =>
                i === 4 ? { ...step, userId: selectedItem?.guid ?? '' } : step
            ),
        }));
        setHasChanges(true);
    };
    const handleFinalReviewChange = (selectedItem: IMultiSelectOption) => {
        setCurrentCaseResolution((prev) => ({
            ...prev,
            steps: prev.steps.map((step, i) =>
                i === 5 ? { ...step, userId: selectedItem?.guid ?? '' } : step
            ),
        }));
        setHasChanges(true);
    };

    useEffect(() => {
        setCurrentCaseResolution(caseResolutionCase);
    }, [caseResolutionCase]);

    if (!caseResolutionCase) {
        return;
    }

    return (
        <>
            <Stack
                direction="horizontal"
                style={{
                    padding: 20,
                    justifyContent: 'space-between',
                    width: '100%',
                    alignItems: 'center',
                    borderBottom: '1px solid #ccc',
                }}
                gap={3}
            >
                <h1 style={{ marginBottom: 0 }}>
                    <CaseName
                        additionalText=" - Case Resolution Settings"
                        caseTitle={CaseHelper.getCaseTitle(currentCase, false)}
                        zoneContext={zoneContext}
                        caseGuid={currentCase.guid}
                    />
                </h1>
                <div>
                    {hasChanges && (
                        <button className="btn btn-orange" onClick={handleSaveButtonClick}>
                            Save
                        </button>
                    )}
                    <button className="btn btn-default" onClick={handleCancelButtonClicked}>
                        Cancel
                    </button>
                </div>
            </Stack>
            <div className="container-fluid">
                <div className="row">
                    <h4
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            paddingLeft: '30px',
                            paddingTop: '30px',
                        }}
                    >
                        Assign Roles
                    </h4>
                </div>
                <div className="row">
                    <div className="col-sm-12">
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'row',
                                gap: '10px',
                            }}
                        >
                            <div
                                style={{ minWidth: 250, maxWidth: 300, width: '100%', margin: 20 }}
                            >
                                <strong>Drafter:</strong>
                                <CheckmateSelect
                                    options={zoneUsers ?? []}
                                    onChange={handleDrafterChange}
                                    value={selectedDrafter}
                                    name="Drafter"
                                />
                            </div>
                            <div
                                style={{ minWidth: 250, maxWidth: 300, width: '100%', margin: 20 }}
                            >
                                <strong>Administrative QC:</strong>
                                <CheckmateSelect
                                    options={zoneUsers ?? []}
                                    onChange={handleParalegalChange}
                                    value={selectedParalegal}
                                    name="RA"
                                />
                            </div>
                            <div
                                style={{ minWidth: 250, maxWidth: 300, width: '100%', margin: 20 }}
                            >
                                <strong>Final Review:</strong>
                                <CheckmateSelect
                                    options={zoneUsers ?? []}
                                    onChange={handleFinalReviewChange}
                                    value={selectedFinalReview}
                                    name="Drafter"
                                />
                            </div>
                        </div>
                    </div>
                </div>
                <div className="row">
                    <div className="col-sm-12">
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'row',
                                gap: '10px',
                            }}
                        >
                            <div
                                style={{ minWidth: 250, maxWidth: 300, width: '100%', margin: 20 }}
                            >
                                <strong>Legal QC:</strong>
                                <CheckmateSelect
                                    options={zoneUsers ?? []}
                                    onChange={handleAttorneyQcChange}
                                    value={selectedAttorneyQC}
                                    name="Drafter"
                                />
                            </div>
                            <div
                                style={{ minWidth: 250, maxWidth: 300, width: '100%', margin: 20 }}
                            >
                                <div>
                                    <strong>A1:</strong>
                                    <Help
                                        type={ApplicationHelpType.Info}
                                        title={'A1'}
                                        helpText={'Case Managers must be assigned in the Case File'}
                                    />
                                </div>
                                <CheckmateSelect
                                    options={zoneUsers ?? []}
                                    onChange={handleAttorneyQcChange}
                                    name="Drafter"
                                    value={selectedA1}
                                    isDisabled={true}
                                />
                            </div>
                            <div
                                style={{ minWidth: 250, maxWidth: 300, width: '100%', margin: 20 }}
                            >
                                <strong>A2:</strong>
                                <Help
                                    type={ApplicationHelpType.Info}
                                    title={'A2'}
                                    helpText={'Case Managers must be assigned in the Case File'}
                                />
                                <CheckmateSelect
                                    options={zoneUsers ?? []}
                                    onChange={handleAttorneyQcChange}
                                    name="Drafter"
                                    value={selectedA2}
                                    isDisabled={true}
                                />
                            </div>
                        </div>

                        {/*Date Selector: Need backend work*/}
                        {/* <input
                    type="date"
                    name={props.assertionDescriptor.guid}
                    className={
                        'form-control' +
                        (caseResolutionCase?.completedDate && Common.isValidDate(firstCA?.dateTimeValue) ? '' : ' unselectClass')
                    }
                    value={firstCA?.dateTimeValue ?? ''}
                    onChange={props.onChangeADTextValue}
                    style={{ width: '100%' }}
                    tabIndex={props.tabIndex}
                /> */}
                    </div>
                </div>
            </div>
            <CheckmateDialog
                isShowingModal={showUnsavedChangesOnCancelDialog}
                title={'Unsaved Changes Detected'}
                body={'You have unsaved changes. Are you sure you want to proceed?'}
                handleClose={() => {
                    setShowUnsavedChangesOnCancelDialog(false);
                }}
                handleConfirm={() => {
                    setShowUnsavedChangesOnCancelDialog(false);
                    onCancel();
                }}
                cancelText={'Cancel'}
                confirmText={'Continue'}
                closeButtonClassName="btn btn-default"
                confirmButtonClassName="btn btn-black"
                size="sm"
                showBackdrop
            />
        </>
    );
}
